import { createSlice } from "@reduxjs/toolkit";
import {
    fetchLearningHubCategories,
    fetchLearningHubFeatured,
    fetchLearningHubArticles,
    fetchLearningHubArticle,
} from "../thunks/learningHubThunk";

const getErrorMessage = (payload, fallback) =>
    payload?.detail || payload?.error || payload?.message || fallback;

const initialState = {
    categories: [],
    featured: [],
    articles: [],
    articlesCount: 0,
    articlesNext: null,
    articlesPrevious: null,
    currentArticle: null,
    listLoading: false,
    listError: null,
    detailLoading: false,
    detailError: null,
    filters: {
        category: "",
        topic: "",
        q: "",
        page: 1,
    },
};

const learningHubSlice = createSlice({
    name: "learningHub",
    initialState,
    reducers: {
        setLearningHubFilters(state, action) {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearLearningHubArticle(state) {
            state.currentArticle = null;
            state.detailError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLearningHubCategories.fulfilled, (state, action) => {
                state.categories = action.payload?.categories || [];
            })
            .addCase(fetchLearningHubFeatured.fulfilled, (state, action) => {
                state.featured = action.payload?.articles || [];
            })
            .addCase(fetchLearningHubArticles.pending, (state) => {
                state.listLoading = true;
                state.listError = null;
            })
            .addCase(fetchLearningHubArticles.fulfilled, (state, action) => {
                state.listLoading = false;
                const page = action.meta?.arg?.page || 1;
                const results = action.payload?.results || [];
                if (page > 1) {
                    const existing = new Set(state.articles.map((item) => item.slug));
                    const merged = [...state.articles];
                    results.forEach((item) => {
                        if (!existing.has(item.slug)) {
                            merged.push(item);
                        }
                    });
                    state.articles = merged;
                } else {
                    state.articles = results;
                }
                state.articlesCount = action.payload?.count ?? state.articles.length;
                state.articlesNext = action.payload?.next ?? null;
                state.articlesPrevious = action.payload?.previous ?? null;
            })
            .addCase(fetchLearningHubArticles.rejected, (state, action) => {
                state.listLoading = false;
                state.listError = getErrorMessage(action.payload, "Could not load articles.");
            })
            .addCase(fetchLearningHubArticle.pending, (state) => {
                state.detailLoading = true;
                state.detailError = null;
            })
            .addCase(fetchLearningHubArticle.fulfilled, (state, action) => {
                state.detailLoading = false;
                state.currentArticle = action.payload;
            })
            .addCase(fetchLearningHubArticle.rejected, (state, action) => {
                state.detailLoading = false;
                state.detailError = getErrorMessage(action.payload, "Article not found.");
                state.currentArticle = null;
            });
    },
});

export const { setLearningHubFilters, clearLearningHubArticle } = learningHubSlice.actions;
export default learningHubSlice.reducer;
